import { InfiniteData } from '@tanstack/react-query';
import { MessagingMessage_ReadCount_Read } from '@treadinc/horizon-api-spec';

import { MessagingMessage } from '~hooks/useMessaging/models';
import { MessagingMessagesPage } from '~queries/useMessagingMessagesInfiniteQuery';

const containsMessage = (
  transcript: InfiniteData<MessagingMessagesPage>,
  messageId: string,
) =>
  transcript.pages.some((page) =>
    page.messages.some((existingMessage) => existingMessage.id === messageId),
  );

// A read only ever adds to a count, so a copy carrying fewer reads is the older one.
const withHighestReadCount = (message: MessagingMessage, existing: MessagingMessage) =>
  message.readCount < existing.readCount
    ? message.withReadCount(existing.readCount)
    : message;

const withMessageReplaced = (
  transcript: InfiniteData<MessagingMessagesPage>,
  message: MessagingMessage,
): InfiniteData<MessagingMessagesPage> => ({
  ...transcript,
  pages: transcript.pages.map((page) => ({
    ...page,
    messages: page.messages.map((existingMessage) =>
      existingMessage.id === message.id
        ? withHighestReadCount(message, existingMessage)
        : existingMessage,
    ),
  })),
});

/**
 * Newest-first ordering; replacing on id stops a socket echo double-bubbling. Never seeds a
 * transcript: one message with no cursor beside it reads as one with no history left to fetch.
 */
export const upsertMessageIntoCache = (
  existing: InfiniteData<MessagingMessagesPage>,
  message: MessagingMessage,
): InfiniteData<MessagingMessagesPage> => {
  if (containsMessage(existing, message.id)) {
    return withMessageReplaced(existing, message);
  }

  const [firstPage, ...remainingPages] = existing.pages;

  return {
    ...existing,
    pages: [
      { ...firstPage, messages: [message, ...firstPage.messages] },
      ...remainingPages,
    ],
  };
};

/** Not an upsert: unshifting unpaged history would render it as the newest message. */
export const replaceMessageInCache = (
  existing: InfiniteData<MessagingMessagesPage> | undefined,
  message: MessagingMessage,
): InfiniteData<MessagingMessagesPage> | undefined => {
  if (!existing || !containsMessage(existing, message.id)) {
    return existing;
  }

  return withMessageReplaced(existing, message);
};

/** Ids outside the loaded pages are skipped; their counts arrive with the page that loads them. */
export const applyReadCountsToCache = (
  existing: InfiniteData<MessagingMessagesPage> | undefined,
  readCounts: MessagingMessage_ReadCount_Read[],
): InfiniteData<MessagingMessagesPage> | undefined => {
  if (!existing) return existing;

  const readCountByMessageId = new Map(
    readCounts.map(({ message_id, read_count }) => [message_id, read_count]),
  );
  let hasChanged = false;

  const pages = existing.pages.map((page) => ({
    ...page,
    messages: page.messages.map((message) => {
      const readCount = readCountByMessageId.get(message.id);

      if (readCount === undefined || readCount === message.readCount) return message;

      hasChanged = true;

      return message.withReadCount(readCount);
    }),
  }));

  return hasChanged ? { ...existing, pages } : existing;
};
